import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LandingNavbarComponent } from '../landing-navbar/navbar.component';
import { Chart, LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, Legend, Title } from 'chart.js';
import { InViewDirective } from '../core/directives/in-view.directive';
import { interval, map, take } from 'rxjs';
import { Meta } from '@angular/platform-browser';

type ScaleBreakpoint = {
    minLength: number;
    scale: number;
};

@Component({
    selector: 'app-horseradish',
    imports: [TranslocoPipe, RouterLink, LandingNavbarComponent, CommonModule, InViewDirective],
    templateUrl: './horseradish.component.html',
    styleUrl: './horseradish.component.css',
})
export class HorseradishComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('horseradishChart') chartElement: ElementRef;

    chartStats = [
        { value: "40%", label: '' },
        { value: "24%", label: '' },
        { value: "30%", label: '' },
    ]

    chart: Chart = null;

    dataManual: number[] = [10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32];
    dataWithHorseradish: number[] = [...this.dataManual];
    chartLabels = {
        title: '',
        horseradish: '',
        manual: ''
    }
    readonly chartAnimationDelay: number = 600;

    activeModelIndex = 0;
    features: Array<{
        title: string;
        content: string;
        image: string;
    }> = [];
    readonly featureImages = [
        '../../assets/images/horseradish/ai.png',
        '../../assets/images/horseradish/api.png',
        '../../assets/images/landing/under-hero-1.jpg',
    ]

    constructor(
        public translate: TranslocoService,
        @Inject(PLATFORM_ID) private platformId: any
    ) {
        // transloco setup for models
        this.translate
            .selectTranslateObject('horseradish.features.list')
            .subscribe((features: Array<{ title: string; content: string }>) => {
                this.features = features.map((feature, i) => ({
                    ...feature,
                    image: this.featureImages[i]
                }));
            });
        // transloco setup for chart stats
        this.translate
            .selectTranslateObject('horseradish.chart.stats')
            .subscribe((translatedStats: Array<string>) => {
                this.chartStats = this.chartStats.map((stat, i) => ({
                    ...stat,
                    label: translatedStats[i]
                }))
            })
        Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Filler, Legend, Title);
    }

    get deliverySolutionsLink() {
        return this.translate.getActiveLang() == 'en' ? 'https://form.typeform.com/to/GQco8dwG?utm_source=radish_frontend' : 'https://form.typeform.com/to/ubaiFTuX?utm_source=radish_frontend'
    }

    ngOnInit() {
        if (!isPlatformBrowser(this.platformId)) return;
        document.body.style.backgroundColor = '#fed1d2';
        this.chart = new Chart('horseradish-chart', {
            type: 'line',
            data: {
                labels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                datasets: [
                    {
                        label: this.chartLabels.manual,
                        data: this.dataManual,
                        borderColor: '#FDE8EC',
                        pointBackgroundColor: '#FDE8EC',
                        // borderColor: '#FAFAFA',
                        // pointBackgroundColor: '#FAFAFA',
                        // borderColor: '#000',
                        // pointBackgroundColor: '#000',
                        borderWidth: 2,
                        pointRadius: 2,
                        fill: true,
                        backgroundColor: (ctx) => {
                            const h = ctx.chart.height;
                            const canvas = ctx.chart.ctx;
                            const gradient = canvas.createLinearGradient(0, -h, h, h);

                            gradient.addColorStop(0, 'rgba(253,232,236,1)');
                            gradient.addColorStop(1, 'rgba(253,232,236,0)');
                            // gradient.addColorStop(0, 'rgba(255,255,255,1)');
                            // gradient.addColorStop(1, 'rgba(255,255,255,0)');
                            // gradient.addColorStop(0, 'rgba(0,0,0,1)');
                            // gradient.addColorStop(1, 'rgba(0,0,0,0)');
                            return gradient;
                        },
                    },
                    {
                        label: this.chartLabels.horseradish,
                        data: this.dataWithHorseradish,
                        borderColor: '#EF1C42',
                        pointBackgroundColor: '#EF1C42',
                        // borderColor: '#EF1C42',
                        // pointBackgroundColor: '#EF1C42',
                        borderWidth: 2,
                        pointRadius: 2,
                        fill: true,
                        backgroundColor: (ctx) => {
                            const h = ctx.chart.height;
                            const canvas = ctx.chart.ctx;
                            const gradient = canvas.createLinearGradient(0, -h, h, h);

                            // gradient.addColorStop(0, 'rgba(255,255,255,1)');
                            // gradient.addColorStop(1, 'rgba(255,255,255,0)');
                            gradient.addColorStop(0, 'rgba(239, 28, 66,1)');
                            gradient.addColorStop(1, 'rgba(239, 28, 66,0)');
                            return gradient;
                        },
                    },

                ],
            },
            options: {
                responsive: true,
                animation: {
                    duration: this.chartAnimationDelay,
                    easing: 'easeInOutQuart'
                },

                scales: {
                    x: {
                        display: false,
                        ticks: {
                            display: false
                        }
                    },
                    y: {
                        display: false,
                        ticks: {
                            display: false
                        },
                        max: 50
                    }
                },
                plugins: {
                    title: {
                        display: true,
                        text: this.chartLabels.title,
                        color: '#000',
                        font: {
                            size: 24,
                            weight: 500
                        }
                    },
                    legend: {
                        position: `bottom`,
                        onClick: () => { },
                        labels: {
                            usePointStyle: true,
                            pointStyle: 'line',
                            color: '#000',
                            font: {
                                size: 14,
                                weight: 600
                            }
                        }
                    }
                }
            },
        });

        this.translate
            .selectTranslateObject('horseradish.chart.labels')
            .subscribe((labels: { title: string; manual: string; horseradish: string }) => {
                this.chartLabels = labels;
                this.updateChartLabels();
            })

    }

    ngAfterViewInit() {
        if (!isPlatformBrowser(this.platformId)) return;

        const options = {
            root: document.querySelector('.models-container'),
            threshold: 0.7
        };
    }

    ngOnDestroy() {
        if (!isPlatformBrowser(this.platformId)) return;
        document.body.style.backgroundColor = '';
    }

    onChartEnterView() {
        if (!this.chart) return;
        const segments = this.dataWithHorseradish.length / 3; // bump it up after every 3 or so
        console.log(segments);
        interval(this.chartAnimationDelay * 1.2).pipe(
            take(segments),
            map(n => n + 1)
        ).subscribe(n => this.updateDataWithHorseradish(n * 3, 4))
    }

    updateChartLabels() {
        this.chart.data.datasets[0].label = this.chartLabels.manual;
        this.chart.data.datasets[1].label = this.chartLabels.horseradish;
        this.chart.options.plugins.title.text = this.chartLabels.title;
        this.chart.update();
    }

    updateDataWithHorseradish(idx: number, increment: number) {
        for (let i = idx; i < this.dataWithHorseradish.length; i++) {
            this.dataWithHorseradish[i] += increment;
        }
        this.chart.data.datasets[1].data = this.dataWithHorseradish;
        this.chart.update();
    }

    // breakpoints NEED to be in decreasing order of maxSize
    scaleLongText(text: string, breakpoints: readonly ScaleBreakpoint[]) {
        const n = text.length;
        for (const { minLength, scale } of breakpoints) {
            if (n > minLength) return scale;
        }
        return 1;
    }

    private readonly CTA_SCALE: readonly ScaleBreakpoint[] = [{ minLength: 30, scale: 0.65 }];
    getHeroCtaScale(t: string) {
        return this.scaleLongText(t, this.CTA_SCALE);
    }

    private readonly TESTIMONIAL_SCALE: readonly ScaleBreakpoint[] = [{ minLength: 100, scale: 0.6 }, { minLength: 100, scale: 0.8 }];
    getTestimonialScale(t: string) {
        return this.scaleLongText(t, this.TESTIMONIAL_SCALE);
    }

    openLink(url: string) {
        window.open(url, '_blank');
    }
}
